import { format, parseISO } from "date-fns";
import {
	Calendar,
	CalendarPlus,
	Clock,
	FolderKanban,
	History,
	Rocket,
	Tag as TagIcon,
} from "lucide-react";
import type { StageKey, Task } from "../../mock/types";
import { STAGES } from "../../mock/data";
import {
	allAssignees,
	formatDueDate,
	formatMinutes,
	projectsById,
	sprintsById,
	typesById,
} from "../../mock/helpers";
import { AvatarStack } from "../AvatarStack";
import { TypeChip } from "../bits";

function Row({
	icon,
	label,
	children,
}: {
	icon: React.ReactNode;
	label: string;
	children: React.ReactNode;
}) {
	return (
		<div className="flex items-start gap-3 py-2.5">
			<div className="flex w-24 shrink-0 items-center gap-1.5 text-xs text-muted">
				{icon}
				{label}
			</div>
			<div className="flex-1 text-sm text-foreground">{children}</div>
		</div>
	);
}

export function MetaRail({
	task,
	onStatusChange,
}: {
	task: Task;
	onStatusChange: (status: StageKey) => void;
}) {
	const assignees = allAssignees(task);
	const project = projectsById.get(task.project_id);
	const sprint = task.sprint_id ? sprintsById.get(task.sprint_id) : null;
	const type = task.task_type_id ? typesById.get(task.task_type_id) : null;

	return (
		<div className="divide-y divide-border">
			<Row
				icon={<span className="h-3.5 w-3.5 rounded-full bg-border-strong" />}
				label="Status"
			>
				<select
					value={task.status}
					onChange={(e) =>
						onStatusChange(e.target.value as StageKey)
					}
					className="h-8 w-full rounded-lg border border-border-strong bg-surface px-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
				>
					{STAGES.map((s) => (
						<option key={s.key} value={s.key}>
							{s.name}
						</option>
					))}
				</select>
			</Row>

			<Row
				icon={<span className="text-[13px] leading-none">👤</span>}
				label="Assignees"
			>
				{assignees.length > 0 ? (
					<div className="flex items-center gap-2">
						<AvatarStack profiles={assignees} size="sm" max={4} />
						<span className="text-xs text-muted">
							{assignees.length} assigned
						</span>
					</div>
				) : (
					<span className="text-sm text-muted">Unassigned</span>
				)}
			</Row>

			{type && (
				<Row
					icon={<span className="text-[13px] leading-none">🏷️</span>}
					label="Type"
				>
					<TypeChip type={type} />
				</Row>
			)}

			<Row icon={<FolderKanban className="h-3.5 w-3.5" />} label="Project">
				{project?.name ?? "—"}
			</Row>

			<Row icon={<Rocket className="h-3.5 w-3.5" />} label="Sprint">
				{sprint?.name ?? (
					<span className="text-muted">No sprint</span>
				)}
			</Row>

			<Row icon={<Calendar className="h-3.5 w-3.5" />} label="Due date">
				{task.due_date ? (
					formatDueDate(task.due_date)
				) : (
					<span className="text-muted">No due date</span>
				)}
			</Row>

			<Row icon={<Clock className="h-3.5 w-3.5" />} label="Estimate">
				{task.estimated_time > 0 ? (
					formatMinutes(task.estimated_time)
				) : (
					<span className="text-muted">Not set</span>
				)}
			</Row>

			<Row icon={<TagIcon className="h-3.5 w-3.5" />} label="Tags">
				{task.tags.length > 0 ? (
					<div className="flex flex-wrap gap-1.5">
						{task.tags.map((t) => (
							<span
								key={t}
								className="rounded bg-muted-subtle px-1.5 py-0.5 text-[11px] text-muted-foreground"
							>
								{t}
							</span>
						))}
					</div>
				) : (
					<span className="text-muted">No tags</span>
				)}
			</Row>

			<Row icon={<CalendarPlus className="h-3.5 w-3.5" />} label="Created">
				{formatTimestamp(task.created_at)}
			</Row>

			<Row icon={<History className="h-3.5 w-3.5" />} label="Modified">
				{formatTimestamp(task.updated_at)}
			</Row>
		</div>
	);
}

function formatTimestamp(value: string) {
	return format(parseISO(value), "MMM d, yyyy h:mm a");
}
